import { useEffect, useState } from "react";
import { api, itemImage } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { History as HistoryIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function History() {
  const [logs, setLogs] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const load = () => api.listWearLog().then(setLogs).catch(() => setLogs([]));
  useEffect(() => { load(); }, []);

  const wornDates = (logs || []).map((l) => new Date(l.date + "T00:00:00"));
  const filtered = selectedDate
    ? (logs || []).filter((l) => l.date === selectedDate.toISOString().slice(0, 10))
    : logs || [];

  const del = async (id) => {
    try { await api.deleteWearLog(id); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em]">What I Wore</h1>
      <p className="text-muted-foreground mt-1">A diary of your outfits.</p>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-1">
          <div className="rounded-[var(--radius)] border border-border bg-card shadow-soft p-4" data-testid="history-calendar">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              modifiers={{ worn: wornDates }}
              modifiersClassNames={{ worn: "bg-good/25 text-foreground rounded-full" }}
            />
            {selectedDate && (
              <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={() => setSelectedDate(null)}>Show all days</Button>
            )}
          </div>
        </div>

        <div className="lg:col-span-2" data-testid="history-outfit-list">
          {!logs ? (
            <div className="space-y-3">{[0,1].map((i)=><Skeleton key={i} className="h-32 rounded-[var(--radius)]"/>)}</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-[var(--radius)] border border-dashed border-border p-12 text-center">
              <HistoryIcon className="h-10 w-10 mx-auto text-muted-foreground" />
              <h3 className="font-display text-xl mt-4">Nothing logged yet</h3>
              <p className="text-sm text-muted-foreground mt-1">Mark outfits as worn from the Planner or Closet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((l) => (
                <div key={l.id} className="rounded-[var(--radius)] border border-border bg-card shadow-soft p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{new Date(l.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
                      {l.occasion && <Badge variant="secondary" className="capitalize">{l.occasion}</Badge>}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => del(l.id)} className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  {l.items && l.items.length > 0 ? (
                    <div className="flex gap-3 overflow-x-auto no-scrollbar">
                      {l.items.map((it) => (
                        <div key={it.id} className="w-20 shrink-0">
                          <div className="aspect-[3/4] rounded-lg overflow-hidden bg-secondary ring-1 ring-black/5">
                            <img src={itemImage(it)} alt={it.name} loading="lazy" className="h-full w-full object-cover" />
                          </div>
                          <div className="text-[10px] mt-1 truncate">{it.name || it.subtype}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Items no longer in closet.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
